import { ICellEditorParams } from '@ag-grid-community/core'
import { Space, Typography } from 'antd'
import Select, { SelectProps } from 'antd/lib/select'
import React, { useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Organization } from '../../../../api'
import { EditingAgGridContext } from '../MarkupRules'

export const FacilitySelector: React.FunctionComponent<
  SelectProps<string | number> & { facilities: Organization[]; valueProp?: keyof Organization }
> = ({ facilities, valueProp = 'id', ...props }) => {
  return (
    <Select
      showSearch
      optionFilterProp="children"
      filterOption={(input, option) =>
        ((option?.searchString as string) ?? '').toLowerCase().includes(input.toLowerCase())
      }
      showArrow={false}
      defaultActiveFirstOption={false}
      dropdownMatchSelectWidth={false}
      style={{ width: '100%' }}
      optionLabelProp="label"
      {...props}>
      {facilities.map(i => (
        <Select.Option
          key={i.id}
          value={i[valueProp || 'id']}
          label={i.name}
          searchString={`${i.name} ${i.facilityId || ''}`}>
          <Space size={0} direction="vertical">
            <Typography.Text>{`${i.name}${i.facilityId ? ` | ${i.facilityId}` : ''}`}</Typography.Text>
            {i.topParent && (
              <Typography.Text type="secondary" title={i.topParent.name}>
                {i.topParent.name}
              </Typography.Text>
            )}
          </Space>
        </Select.Option>
      ))}
    </Select>
  )
}

export const AgFacilitySelector = React.forwardRef<
  {},
  ICellEditorParams & { powerList: Organization[]; topParentOnly: boolean }
>((params, ref) => {
  const [value, setValue] = useState<string | number>(params.value)
  const { onChangeEditingItem, editingItem } = useContext(EditingAgGridContext)

  const facilities = useMemo(() => {
    if (params.topParentOnly) {
      return params.powerList.filter(i => !i.topParent)
    }
    return params.powerList.filter(
      i => i.facilityId && (!editingItem?.topParentId || editingItem.topParentId === i.topParent?.id),
    )
  }, [params.powerList, params.topParentOnly, editingItem])

  useEffect(() => {
    if (params.topParentOnly && editingItem?.topParent && !params.value) {
      setValue(editingItem.topParent.id)
    }
  }, [editingItem, params.topParentOnly, params.value])

  useImperativeHandle(ref, () => ({
    getValue: () => value,
  }))

  return (
    <FacilitySelector
      allowClear
      value={value}
      valueProp={params.topParentOnly ? 'id' : 'facilityId'}
      placeholder={params.topParentOnly ? 'Select Top Parent' : 'Select Facility'}
      notFoundContent={'No facilities found'}
      onChange={v => {
        if (params.topParentOnly) {
          const facility = params.powerList.find(i => i.id === v)
          onChangeEditingItem(prev => ({
            ...prev,
            topParentId: v as string,
            topParent: facility,
          }))
        } else {
          const facility = params.powerList.find(i => i.facilityId === Number(v))
          onChangeEditingItem(prev => ({
            ...prev,
            facilityId: facility?.facilityId,
            facility: facility,
            topParentId: facility?.topParent?.id,
            topParent: facility?.topParent,
          }))
        }
        setValue(v)
      }}
      facilities={facilities}
    />
  )
})
